import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { t } from '../i18n.js';
import Wachtwoordveld from '../components/Wachtwoordveld.jsx';
import logo from '../assets/adts-logo-wit.png';

/**
 * Twee wegen naar binnen, en ze zijn niet even belangrijk (zoals in ADTS-ONE). **Het ADTS-account
 * is de weg.** De noodingang staat achter een regel tekst en vraagt naast het wachtwoord een code
 * uit de authenticator-app.
 *
 * Op de publieke ingang (docs/besluiten/klanten-via-het-portaal.md) is er geen van beide: een klant komt binnen via
 * het klantportaal. De server zegt op welke ingang dit scherm staat.
 */
export default function Aanmelden({ onGelukt }) {
  const [nummer, zetNummer] = useState('');
  const [wachtwoord, zetWachtwoord] = useState('');
  const [code, zetCode] = useState('');
  const [fout, zetFout] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const [wegen, zetWegen] = useState(null);
  const [noodingang, zetNoodingang] = useState(false);
  /* Waarom de vorige poging terugkwam; welke tekst daarbij hoort, hangt af van de ingang. */
  const [reden, zetReden] = useState(null);

  useEffect(() => {
    api.aanmeldwegen().then(({ data }) => zetWegen(data)).catch(() => zetWegen({ ingang: 'intern', oidc: { enabled: false } }));
    /* De terugweg van de aanbieder eindigt hier met een reden; die gaat er meteen weer af. */
    const reden = new URLSearchParams(window.location.search).get('aanmelden');
    if (reden) {
      zetReden(reden);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function verstuur(e) {
    e.preventDefault();
    zetFout(null);
    zetBezig(true);
    try {
      await api.aanmelden(nummer.trim(), wachtwoord, code.trim());
      onGelukt();
    } catch (err) {
      /* De server zegt met opzet niet welk deel niet klopte. */
      zetFout(err.status === 401 ? t('Nummer, wachtwoord of code klopt niet') : t(err.message));
      zetBezig(false);
    }
  }

  /* Pas tonen als bekend is welke ingang dit is: anders flitst de noodingang ook op de publieke. */
  if (!wegen) return <div className="aanmeld" />;
  if (wegen.ingang === 'publiek') return <ViaPortaal portaal={wegen.portaal} reden={reden} />;
  const melding = fout ?? (reden && (reden === 'uit'
    ? t('Aanmelden met het ADTS-account staat niet aan.')
    : t('Aanmelden met het ADTS-account is niet gelukt. Probeer het opnieuw of vraag een beheerder.')));

  const viaAanbieder = wegen?.oidc?.enabled;
  const formulier = !viaAanbieder || noodingang;

  return (
    <div className="aanmeld">
      <img className="aanmeldlogo" src={logo} alt="ADTS" width="148" height="45" />
      <form onSubmit={verstuur}>
        <h1>ZERO <span className="licht">Insight</span></h1>
        <p className="sub">{t('Detectie en respons voor ADTS en klanten')}</p>
        {melding && <div className="melder rood">{melding}</div>}

        {viaAanbieder && (
          <>
            {/* Een gewone link: de aanbieder stuurt de browser weg en weer terug. */}
            <a className="viaknop" href="/api/v1/auth/oidc/start">{t('Aanmelden met')} {wegen.oidc.label}</a>
            <p className="hint">{t('ADTS ZERO bewaart geen wachtwoord. Wordt het account geblokkeerd, dan is de toegang meteen weg.')}</p>
          </>
        )}

        {formulier ? (
          <>
            {viaAanbieder && <div className="of">{t('Noodingang')}</div>}
            <label>{t('Medewerkernummer')}
              <input value={nummer} onChange={(e) => zetNummer(e.target.value)} autoComplete="username" autoFocus placeholder="Z-001" />
            </label>
            <label>{t('Wachtwoord')}<Wachtwoordveld waarde={wachtwoord} zet={zetWachtwoord} /></label>
            <label>{t('Code uit de authenticator-app')}
              <input value={code} onChange={(e) => zetCode(e.target.value)} inputMode="numeric"
                autoComplete="one-time-code" placeholder="123 456" maxLength={7} />
            </label>
            <button disabled={bezig || !nummer || !wachtwoord || code.replace(/\s/g, '').length !== 6}>
              {bezig ? t('Bezig met aanmelden…') : t('Aanmelden')}
            </button>
          </>
        ) : (
          <button type="button" className="noodknop" onClick={() => zetNoodingang(true)}>
            {t('Aanmelden met een medewerkernummer')}
          </button>
        )}
      </form>
    </div>
  );
}

/** Wat het portaal of ZERO als reden gaf (`?aanmelden=` na de terugkomst), in woorden voor de klant. */
const portaalreden = (reden) => ({
  not_permitted: t('Je hebt in het klantportaal geen toegang tot ADTS ZERO. Vraag de beheerder van je organisatie.'),
  mfa_required: t('ADTS ZERO vraagt tweestapsverificatie. Stel die in het klantportaal in en probeer het opnieuw.'),
  reauthentication_required: t('Meld je opnieuw aan in het klantportaal, met de code uit je authenticator-app.'),
  no_account: t('Je organisatie heeft nog geen toegang tot ADTS ZERO. Neem contact op met ADTS.'),
  geen_klant: t('Je organisatie heeft nog geen toegang tot ADTS ZERO. Neem contact op met ADTS.'),
  impersonation: t('Meekijken in het klantportaal opent ADTS ZERO niet.'),
  uit: t('Aanmelden via het klantportaal staat niet aan.'),
})[reden] ?? t('Aanmelden via het klantportaal is niet gelukt. Probeer het opnieuw.');

/**
 * De publieke ingang. Aanmelden gaat via het klantportaal (OIDC); zolang dat niet aan staat, staat hier de weg naar
 * het portaal en geen formulier.
 *
 * **Een knop, geen automatische doorverwijzing.** Wie in ZERO afmeldt maar in het portaal nog is aangemeld, zou
 * anders meteen weer binnen zijn: afmelden zou dan niets doen.
 */
function ViaPortaal({ portaal, reden }) {
  return (
    <div className="aanmeld">
      <img className="aanmeldlogo" src={logo} alt="ADTS" width="148" height="45" />
      <form onSubmit={(e) => e.preventDefault()}>
        <h1>ZERO <span className="licht">Insight</span></h1>
        <p className="sub">{t('Detectie en respons voor ADTS en klanten')}</p>
        {reden && <div className="melder rood">{portaalreden(reden)}</div>}
        {portaal.enabled ? (
          <a className="viaknop" href="/api/v1/auth/portal/start">{t('Aanmelden via het klantportaal')}</a>
        ) : (
          <>
            <p className="hint">{t('Klanten komen in ADTS ZERO via het klantportaal. Die koppeling is er nog niet.')}</p>
            <a className="viaknop" href={portaal.url}>{t('Naar het klantportaal')}</a>
          </>
        )}
        <p className="hint">{t('Medewerker van ADTS? Meld je aan op het ADTS-netwerk of via de VPN.')}</p>
      </form>
    </div>
  );
}
